import React from 'react';
import ReportPage from '../ReportChart/ReportPage';
import { Apis } from 'services/api';
import API from "helpers/api";
import { getAPIResponse } from 'helpers/getApiResponse';
import { useChartColors } from '../ReportChart/hooks';

// Helper function to build department IDs query string
const buildDepartmentQuery = (siteDepartmentId = "", siteDepartmentIds = []) => {
	if (siteDepartmentIds && siteDepartmentIds.length > 0) {
		const query = siteDepartmentIds.map(id => `siteDepartmentIds=${id}`).join('&');
		return query ? `&${query}` : "";
	} else if (siteDepartmentId) {
		return `&siteDepartmentId=${siteDepartmentId ?? ""}`;
	}
	return "";
};

const getServiceTimes = async ({
	serviceId = "",
	depth = "",
	modelVersionStageId = "",
	modelVersionZoneId = "",
	taskId = "",
	siteDepartmentId = "",
	siteDepartmentIds = [],
}) => {
  try {
    // Make sure serviceId is defined before calling API
    if (!serviceId) {
      return {
        status: false,
        data: { message: "Service ID is required" }
      };
    }

		const deptQuery = buildDepartmentQuery(siteDepartmentId, siteDepartmentIds);
    let response = await API.get(
			`${Apis.Services}/${serviceId}/servicetimes?depth=${depth}&modelVersionStageId=${modelVersionStageId}&modelVersionZoneId=${modelVersionZoneId}&taskId=${taskId}${deptQuery}`
		);
    return getAPIResponse(response);
  } catch (err) {
    return getAPIResponse(err?.response);
  }
};

const ServiceTimesReport = ({ 
  initialServiceId, 
  useFilters = false, 
  analyticsMode = false,
  filtersConfig = {},
  customCrumbs = [],
  useDefaultNavigation = false,
  customTitle = null
}) => {
  const { customCaptions } = JSON.parse(sessionStorage.getItem("me")) || JSON.parse(localStorage.getItem("me"));
  const { getColorsByCategory } = useChartColors();
  
  // Use initialServiceId if provided (for direct service views), otherwise null
  const [serviceId] = React.useState(initialServiceId);

  if (!serviceId && !useFilters) {
    console.error('ServiceTimesReport: Either initialServiceId or useFilters must be provided');
  }

  // Use the provided custom title or create a default one
  const title = customTitle || `${customCaptions?.service || 'Service'} Times`;

  const chartConfig = {
    id: 'service-times',
    title,
    drilldownLabel: (dataPoint) => dataPoint 
    ? `${dataPoint?.payload?.label}`
    : `${customCaptions.service || 'Service'}`,
    initialFilters: serviceId ? { serviceId } : {},
    showFilters: useFilters,
    filters: useFilters ? filtersConfig : {},
    
    fetchData: async (params) => {
      // If we have initialServiceId, use it (service detail page)
      if (serviceId) {
        return await getServiceTimes({
          ...params,
          serviceId
        });
      }
      
      // If we're in analytics mode, check if serviceId is provided via filters
      if (analyticsMode) {
        if (!params.serviceId) {
          return {
            status: false,
            invalidFilters: true,
            message: `Please select a ${customCaptions?.service || 'Service'}`
          };
        }
        return await getServiceTimes(params);
      }
      
      // Fallback error case
      return {
        status: false,
        data: { message: "Service ID is required" }
      };
    },
    allowedChartTypes: ['vertical-bar', 'horizontal-bar'],
    defaultChartType: 'horizontal-bar', 
    axisConfig: { 
      categoryAxis: {
        dataKey: 'label',
        type: 'category',
      },
      valueAxes: [
        {
          label: 'Minutes', 
        }
      ]
    },
    series: [ 
      {
        key: 'estimatedMinutes',
        label: 'Estimated Time (Minutes)',
        type: 'bar',
        colors: [getColorsByCategory('primary')[0]],
      },
      {
        key: 'actualMinutes',
        label: 'Actual Time (Minutes)',
        type: 'bar',
        colors: [getColorsByCategory('primary')[1]],
        stackId: 'stack1',
      },
      {
        key: 'pauseMinutes',
        label: 'Pause Time (Minutes)',
        type: 'bar',
        colors: [getColorsByCategory('neutral')[0]],
        cursor: 'default',
        stackId: 'stack1'
      }
    ],
    transformData: (response) => {
      if (!response?.data) return null;

      // Handle case when we get an object with depth value but no data array
      if (response.data.depth && !response.data.data) {
        const transformedData = [];
        
        if ('totalEstimatedMinutes' in response.data || 'totalActualMinutes' in response.data) {
          // Get service number from response if available
          const serviceName = response.data.service ? `${customCaptions?.service} ${response.data.service}` : 'Service';
          
          transformedData.push({
            id: 'S',
            name: serviceName,
            label: serviceName,
            estimatedMinutes: parseFloat((response.data.totalEstimatedMinutes || 0).toFixed(2)),
            actualMinutes: parseFloat((response.data.totalActualMinutes || 0).toFixed(2)),
            pauseMinutes: parseFloat((response.data.totalPauseMinutes || 0).toFixed(2)),
            depth: response.data.depth,
            seriesKey: 'estimatedMinutes' // Add seriesKey for debugging
          });
        }
        
        return transformedData;
      }
      
      // Handle regular case with data array
      if (!response.data.data) return null;

      const transformedData = response.data.data.map(item => ({
        id: item.id,
        name: item.name,
        label: item.name,
        estimatedMinutes: parseFloat((item.estimatedMinutes || 0).toFixed(2)),
        actualMinutes: parseFloat((item.actualMinutes || 0).toFixed(2)),
        pauseMinutes: 0, // Initialize pauseMinutes with 0
        depth: response.data.depth,
        seriesKey: 'estimatedMinutes' // Add seriesKey for debugging
      }));

      // If we have pause minutes, assign it to the pause time record
      // This is only needed if we want to show a separate pause bar
      /* 
      if (response.data.totalPauseMinutes && (response.data.depth === 'S' || response.data.depth === 'V')) {
        transformedData.push({
          id: 'pause',
          name: 'Pause Time',
          pauseMinutes: parseFloat((response.data.totalPauseMinutes || 0).toFixed(2)),
          depth: response.data.depth,
          _isPauseData: true
        });
      }
      */
      
      // Distribute pause time to the summary item if there's only one item
      if (response.data.totalPauseMinutes && transformedData.length === 1) {
        transformedData[0].pauseMinutes = parseFloat((response.data.totalPauseMinutes || 0).toFixed(2));
      }
      // If there's a summary item with id='summary', assign all pause time to it
      else if (response.data.totalPauseMinutes) {
        const summaryItem = transformedData.find(item => item.id === 'S');
        if (summaryItem) {
          summaryItem.pauseMinutes = parseFloat((response.data.totalPauseMinutes || 0).toFixed(2));
        } 
        // Otherwise create a separate item for total pause time
        else if (response.data.depth === 'S' || response.data.depth === 'V') {
          transformedData.push({
            id: 'S',
            name: 'Pause Time',
            label: 'Pause Time',
            pauseMinutes: parseFloat((response.data.totalPauseMinutes || 0).toFixed(2)),
            actualMinutes: 0,
            estimatedMinutes: 0,
            depth: response.data.depth
          });
        }
      }

      return transformedData;
    },
    
    drilldown: {
      defaultChartType: 'vertical-bar',
      allowedChartTypes: ['vertical-bar', 'horizontal-bar'],
      axisConfig: {
        categoryAxis: { dataKey: 'label', type: 'category' },
        valueAxes: [{ label: 'Minutes' }] 
      },
      series: [
        {
          key: 'estimatedMinutes',
          label: 'Estimated Time (Minutes)',
          type: 'bar',
          colors: [getColorsByCategory('primary')[0]],
          drilldownLabel: (dataPoint) => dataPoint?.name || 'Task'
        },
        {
          key: 'actualMinutes',
          label: 'Actual Time (Minutes)',
          type: 'bar',
          colors: [getColorsByCategory('primary')[1]],
          stackId: 'stack1',
          drilldownLabel: (dataPoint) => dataPoint?.name || 'Task'
        },
        {
          key: 'pauseMinutes',
          label: 'Pause Time (Minutes)',
          type: 'bar',
          colors: [getColorsByCategory('neutral')[0]],
          stackId: 'stack1'
        }
      ],
      drilldownLabel: (dataPoint) => dataPoint 
      ? `${dataPoint?.payload?.label}`
      : `${customCaptions.stagesPlural || 'Stages'}`,
      fetchData: async (params) => {
        // If we have initialServiceId, use it (service detail page)
        if (serviceId) {
          return await getServiceTimes({
            ...params,
            serviceId
          });
        } else if (analyticsMode) {
          if (!params.serviceId) {
            return {
              status: false,
              invalidFilters: true,
              message: `Please select a ${customCaptions?.service || 'Service'}`
            };
          }
          return await getServiceTimes(params);
        }
        
        return {
          status: false,
          data: { message: "Service ID is required" }
        };
      },
      getParams: (dataPoint, currentParams = {}) => {
        if (!dataPoint) return currentParams;
        
        const depth = dataPoint.depth || 'V';
        
        if (depth === 'V') {
          return {
            ...currentParams,
            depth: dataPoint.id || ''
          };
        } else if (depth === 'S') {
          return {
            ...currentParams,
            modelVersionStageId: dataPoint.id || ''
          };
        } else if (depth === 'Z') {
          // Use dedicated modelVersionZoneId property or fall back to id
          const zoneId = dataPoint.modelVersionZoneId || dataPoint.id || '';
          
          return {
            ...currentParams,
            modelVersionZoneId: zoneId
          };
        }
        
        return currentParams;
      },
      transformData: (response) => {
        if (!response?.data) return null;
        
        // Handle case when we get an object with depth value but no data array
        if (response.data.depth && !response.data.data) {
          const transformedData = [];
          
          if ('totalEstimatedMinutes' in response.data || 'totalActualMinutes' in response.data) {
            // Get service number from response if available
            const serviceName = response.data.service ? `${customCaptions?.service} ${response.data.service}` : 'Service';
            
            transformedData.push({
              id: 'S',
              name: serviceName,
              label: serviceName,
              estimatedMinutes: parseFloat((response.data.totalEstimatedMinutes || 0).toFixed(2)),
              actualMinutes: parseFloat((response.data.totalActualMinutes || 0).toFixed(2)),
              pauseMinutes: parseFloat((response.data.totalPauseMinutes || 0).toFixed(2)),
              depth: response.data.depth,
              seriesKey: 'estimatedMinutes',
            });
          }
          
          return transformedData;
        }
        
        // Handle regular case with data array
        if (!response.data.data) return null;
    
        const transformedData = response.data.data.map(item => ({
          id: item.id,
          name: item.name,
          label: item.name,
          estimatedMinutes: parseFloat((item.estimatedMinutes || 0).toFixed(2)),
          actualMinutes: parseFloat((item.actualMinutes || 0).toFixed(2)),
          pauseMinutes: 0,
          depth: response.data.depth,
          seriesKey: 'estimatedMinutes',
          hasZones: item.hasZones
        }));
        
        // Distribute pause time if available
        if (response.data.totalPauseMinutes && transformedData.length === 1) {
          transformedData[0].pauseMinutes = parseFloat((response.data.totalPauseMinutes || 0).toFixed(2));
        }
        else if (response.data.totalPauseMinutes) {
          const summaryItem = transformedData.find(item => item.id === 'S');
          if (summaryItem) {
            summaryItem.pauseMinutes = parseFloat((response.data.totalPauseMinutes || 0).toFixed(2));
          } else {
            transformedData.push({
              id: 'S',
              name: 'Pause Time',
              label: 'Pause Time',
              pauseMinutes: parseFloat((response.data.totalPauseMinutes || 0).toFixed(2)),
              actualMinutes: 0,
              estimatedMinutes: 0,
              depth: response.data.depth,
              seriesKey: 'pauseMinutes'
            });
          }
        }

        return transformedData;
      },
      drilldown: {
        defaultChartType: 'horizontal-bar',
        allowedChartTypes: ['horizontal-bar', 'vertical-bar'],
        axisConfig: {
          categoryAxis: { dataKey: 'label', type: 'category', width: 200 },
          valueAxes: [{ label: 'Minutes' }] 
        },
        series: [
          {
            key: 'estimatedMinutes',
            label: 'Estimated Time (Minutes)',
            type: 'bar',
            colors: [getColorsByCategory('primary')[0]],
            drilldownLabel: (dataPoint) => dataPoint?.name || 'Task'
          },
          {
            key: 'actualMinutes',
            label: 'Actual Time (Minutes)',
            type: 'bar',
            colors: [getColorsByCategory('primary')[1]],
            stackId: 'stack1',
            drilldownLabel: (dataPoint) => dataPoint?.name || 'Task'
          },
          {
            key: 'pauseMinutes',
            label: 'Pause Time (Minutes)',
            type: 'bar',
            colors: [getColorsByCategory('neutral')[0]],
            stackId: 'stack1'
          }
        ],
        drilldownLabel: (dataPoint, parentDataPoints) => {
            if (dataPoint) {
              return `${dataPoint?.payload?.label}`;
            }
            
            // Check if the parent dataPoint has hasZones=true
            const parentDataPoint = parentDataPoints && parentDataPoints.length > 0 ? parentDataPoints[parentDataPoints.length - 1] : null;
            
            if (parentDataPoint && parentDataPoint.hasZones === false) {
              return `${customCaptions.tasksPlural || 'Tasks'}`;
            }
            
            return `${customCaptions.zonesPlural || 'Zones'}`;
          },
          disableDrilldown: (parentDataPoints) => {
            // Disable further drilldown if the parent has hasZones=false
            const parentDataPoint = parentDataPoints && parentDataPoints.length > 0 ? parentDataPoints[parentDataPoints.length - 1] : null;
            return parentDataPoint && parentDataPoint.hasZones === false;
          },
        fetchData: async (params) => {
          // If we have initialServiceId, use it (service detail page)
          if (serviceId) {
            return await getServiceTimes({
              ...params,
              serviceId
            });
          } else if (analyticsMode) {
            if (!params.serviceId) {
              return {
                status: false,
                invalidFilters: true,
                message: `Please select a ${customCaptions?.service || 'Service'}`
              };
            }
            return await getServiceTimes(params);
          }
          
          return {
            status: false,
            data: { message: "Service ID is required" }
          };
        },
        getParams: (dataPoint, currentParams = {}) => {
          if (!dataPoint) return currentParams;
          
          const depth = dataPoint.depth || 'V';
          
          if (depth === 'V') {
            return {
              ...currentParams,
              depth: dataPoint.id || ''
            };
          } else if (depth === 'S') {
            return {
              ...currentParams,
              modelVersionStageId: dataPoint.id || ''
            };
          } 
          else if (depth === 'Z') {
            // Use dedicated modelVersionZoneId property or fall back to id
            const zoneId = dataPoint.modelVersionZoneId || dataPoint.id || '';
            
            return {
              ...currentParams,
              modelVersionZoneId: zoneId
            };
          }
          
          return currentParams;
        },
        drilldown: {
          defaultChartType: 'horizontal-bar',
          allowedChartTypes: ['horizontal-bar', 'vertical-bar'],
          axisConfig: {
            categoryAxis: { dataKey: 'label', type: 'category', width: 200 },
            valueAxes: [{ label: 'Minutes' }] 
          },
          series: [
            {
              key: 'estimatedMinutes',
              label: 'Estimated Time (Minutes)',
              type: 'bar',
              colors: [getColorsByCategory('primary')[0]],
              drilldownLabel: (dataPoint) => dataPoint?.name || 'Action'
            },
            {
              key: 'actualMinutes',
              label: 'Actual Time (Minutes)',
              type: 'bar',
              colors: [getColorsByCategory('primary')[1]],
              stackId: 'stack1',
              drilldownLabel: (dataPoint) => dataPoint?.name || 'Action'
            },
            {
              key: 'pauseMinutes',
              label: 'Pause Time (Minutes)',
              type: 'bar',
              colors: [getColorsByCategory('neutral')[0]],
              stackId: 'stack1'
            }
          ],
          drilldownLabel: () => `${customCaptions.taskPlural || 'Tasks'}`,
          fetchData: async (params) => {
            // If we have initialServiceId, use it (service detail page)
            if (serviceId) {
              return await getServiceTimes({
                ...params,
                serviceId
              });
            } else if (analyticsMode) {
              if (!params.serviceId) {
                return {
                  status: false,
                  invalidFilters: true,
                  message: `Please select a ${customCaptions?.service || 'Service'}`
                };
              }
              return await getServiceTimes(params);
            }
            
            return {
              status: false,
              data: { message: "Service ID is required" }
            };
          },
          getParams: (dataPoint, currentParams = {}) => {
            if (!dataPoint) return currentParams;
            
            const depth = dataPoint.depth || 'T';
            
            if (depth === 'T') {
              return {
                ...currentParams,
                taskId: dataPoint.id || ''
              };
            }
            
            return currentParams;
          }
        }
      }
    }
  };

  return (
    <ReportPage 
      title={title}
      chartConfig={chartConfig}
      customCrumbs={customCrumbs}
      hideNav={!useDefaultNavigation && serviceId && customCrumbs.length === 0} // Hide navigation except for analytics view
    />
  );
};

export default ServiceTimesReport;